import defaultContent from '../../../content/permissionRoleList';
import { useTranslation } from 'react-i18next';
import Title from '../../common/Title/Title';
import Button from '../../common/Button/Button';
import { useGetFeaturesQuery } from '../../../redux/services/featuresAPI';
import {
  FeatureRecord,
  RoleFeatureRecordView,
  RoleRecord,
  RoleRecordView,
} from '../../../redux/services/types/types';
import { useDeleteRoleMutation, useGetRolesQuery } from '../../../redux/services/rolesAPI';
import { ColumnDef } from '../../common/Table/useTable';
import IconButton from '../../common/IconButton/IconButton';
import { GeneralStatusPill } from '../../common/TableUtils/TableUtils';
import Table, { TableActions } from '../../common/Table/Table';
import { useNavigate } from 'react-router';
import Icon from '../../common/Icon/Icon';
import styles from './PermissionRoleList.module.scss';
import { useState } from 'react';
import { useToast } from '../../common/Toast/ToastContext';
import Confirm from '../../common/Confirm/Confirm';

export const PermissionRoleList = () => {
  const { t } = useTranslation(['permissionRoleList']);
  const title = t('permissionRoleList', defaultContent['permissionRoleList']).toString();
  const navigate = useNavigate();

  const { featureData, isFeatureDataLoading } = GetFeatureList();

  const { roleData, isRoleLoading } = GetAndTransformRoleDataList(featureData);

  const colDefs: Array<ColumnDef<RoleRecordView>> = [
    { field: 'name', header: 'Name' },
    {
      field: 'status',
      header: 'Status',
      renderCell: (record) => <GeneralStatusPill status={record.status} />,
    },
    {
      header: 'Actions',
      renderCell: (record, colDef) => (
        <TableActions>
          <IconButton
            disabled={false}
            icon="edit"
            action={() => navigate(`/permission/role/detail?id=${record.permissionRoleId}`)}
            data-testid={`edit-button-${record.permissionRoleId}`}
          />
          <DeleteRoleButton id={record.permissionRoleId} />
        </TableActions>
      ),
    },
  ];

  const permissionRoleList = (
    <div className="container">
      <Title text={title} />
      <div className="h-3"></div>
      <div className="row">
        <div className="column-one right">
          <span className="spacing-half-one"></span>
          <Button
            text="Add Role"
            color="primary-black"
            disabled={false}
            action={function (): void {
              navigate('/permission/role/detail');
            }}
          />
          <span className="spacing-half-one"></span>
        </div>
      </div>
      <div className="h-2"></div>
      <Table
        colDefs={colDefs}
        data={roleData || []}
        isLoading={isFeatureDataLoading || isRoleLoading}
        getRowId={(role) => role.permissionRoleId.toString()}
        isExpandable={true}
        renderSubRow={RenderFeatureRoleTable}
      />
      <div className="h-3"></div>
    </div>
  );

  return <>{permissionRoleList}</>;
};

const RenderFeatureRoleTable = (row: RoleRecordView) => {
  const data = row.permissionRoleFeatures;

  const colDefs: Array<ColumnDef<RoleFeatureRecordView>> = [
    { field: 'permissionFeatureName', header: 'Name' },
    { field: 'applicationName', header: 'Application Name' },
    {
      field: 'insert',
      header: 'Create',
      renderCell: (record) => (record.insert === 1 ? <Icon name="check" /> : null),
    },
    {
      field: 'select',
      header: 'Read',
      renderCell: (record) => (record.select === 1 ? <Icon name="check" /> : null),
    },
    {
      field: 'update',
      header: 'Update',
      renderCell: (record) => (record.update === 1 ? <Icon name="check" /> : null),
    },
    {
      field: 'delete',
      header: 'Delete',
      renderCell: (record) => (record.delete === 1 ? <Icon name="check" /> : null),
    },
    {
      field: 'status',
      header: 'Status',
      renderCell: (record) => <GeneralStatusPill status={record.status} />,
    },
  ];

  return (
    <div
      className={`${styles['role-feature-table']}`}
      data-test-id={`role-feature-table-${row.permissionRoleId}`}
    >
      <Table colDefs={colDefs} data={data || []} />
    </div>
  );
};

const GetFeatureList = () => {
  const { data, isLoading } = useGetFeaturesQuery();

  return { featureData: data, isFeatureDataLoading: isLoading };
};

const GetAndTransformRoleDataList = (featureData: FeatureRecord[] | undefined) => {
  const { data, isLoading } = useGetRolesQuery();

  let roleData: RoleRecordView[] = [];

  if (data && data.length > 0) {
    const datas = data as RoleRecord[];
    roleData = datas.map((role) => {
      const roleView = {
        permissionRoleId: role.permissionRoleId,
        name: role.name,
        status: role.status,
        permissionRoleFeatures: role.permissionRoleFeatures.map((feature) => {
          const featureModel = featureData?.find(
            (x) => x.permissionFeatureId === feature.permissionFeatureId,
          );
          const featureName = featureModel ? featureModel.name : '';
          const applicationName = featureModel ? featureModel.applicationName : '';

          return {
            permissionRoleFeatureId: feature.permissionRoleFeatureId,
            permissionFeatureId: feature.permissionFeatureId,
            insert: feature.insert,
            select: feature.select,
            update: feature.update,
            delete: feature.delete,
            status: feature.status,
            permissionFeatureName: featureName,
            applicationName,
          };
        }),
        audit: role.audit,
      };

      return roleView;
    });
  }

  return { roleData, isRoleLoading: isLoading };
};

export const DeleteRoleButton = ({ id }: { id: number }) => {
  const [deleteRole, { isLoading }] = useDeleteRoleMutation();
  const { refetch } = useGetRolesQuery();

  const [confirm, setConfirm] = useState(false);

  const { addToast } = useToast();

  const onDelete = () =>
    deleteRole(id).then((res) => {
      if ('error' in res) {
        addToast({
          content: 'Failed to delete role !',
          type: 'error',
        });
      } else {
        refetch();
        setConfirm(false);
        addToast({
          content: 'Role deleted successfully !',
          type: 'success',
        });
      }
    });

  return (
    <>
      <IconButton
        disabled={isLoading}
        icon="delete"
        action={() => setConfirm(true)}
        data-testid="delete-icon"
      />
      <Confirm
        open={confirm}
        title="Delete Role?"
        description="Are you sure you want to permanently delete this role? This action cannot be undone."
        confirmText="Yes, Delete"
        cancelText="No, Cancel"
        onConfirm={onDelete}
        onCancel={() => setConfirm(false)}
      />
    </>
  );
};

export default PermissionRoleList;
